import { Injectable, signal, inject } from '@angular/core';
import { 
  ChatConversation, 
  Message, 
  ChatRequest, 
  ChatResponse, 
  UserThreadsResponse, 
  ChatHistoryResponse, 
  ThreadDomain 
} from '../features/chatbot/models/chatbot.models';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable, tap, map, catchError, of, forkJoin } from 'rxjs';
import { AuthService } from './auth-service';
import { EngineService } from './engine-service';
import { EquipoService } from './equipo-service';

@Injectable({
  providedIn: 'root'
})
export class ChatbotService {
  private readonly authService = inject(AuthService);
  private readonly engineService = inject(EngineService);
  private readonly equipoService = inject(EquipoService);
  
  conversations = signal<ChatConversation[]>([]);
  currentConversationId = signal<string | null>(null);
  pendingQuestion = signal<string | null>(null);
  pendingMotorRef = signal<string | null>(null);
  pendingDomain = signal<ThreadDomain | null>(null);
  pendingItemDetails = signal<{ brand: string, model: string, serialNumber: string, type: 'motor' | 'equipo' } | null>(null);

  constructor(private http: HttpClient) { }

  /**
   * Carga los hilos del usuario. Permite filtrar por tipo: 'technical' | 'help'
   */
  loadUserThreads(): Observable<UserThreadsResponse> {
    const userId = this.authService.userSignal()?._id;
    if (!userId) {
      throw new Error('No user logged in');
    }

    return this.http.get<UserThreadsResponse>(
      `${environment.API_CHATBOT_URL}/api-chatbot/user-threads/${userId}`
    ).pipe(
      tap(response => {
        const loadedConversations: ChatConversation[] = response.threads.map(thread => ({
          id: thread.thread_id,
          title: thread.title || 'Conversación anterior',
          messages: [],
          lastUpdated: new Date(thread.created_at)
        }));
        this.conversations.set(loadedConversations);
      })
    );
}

  getItemDetails(id: string): Observable<{ brand: string, model: string, serialNumber: string, type: 'motor' | 'equipo' } | null> {
    return forkJoin({
      engine: this.engineService.getEngineById(id).pipe(catchError(() => of(null))),
      equipo: this.equipoService.getEquipoById(id).pipe(catchError(() => of(null)))
    }).pipe(
      map(results => {
        if (results.engine) {
          const e = results.engine as any;
          return { brand: e.brand, model: e.model, serialNumber: e.serialNumber, type: 'motor' as const };
        }
        if (results.equipo) {
          const q = results.equipo as any;
          return { brand: q.brand, model: q.model, serialNumber: q.serialNumber, type: 'equipo' as const };
        }
        return null;
      })
    );
  }

  /**
   * Carga el historial de un thread. Funciona tanto para 'technical' como para 'help'
   */
  loadChatHistory(threadId: string): Observable<ChatHistoryResponse> {
    return this.http.get<ChatHistoryResponse>(`${environment.API_CHATBOT_URL}/api-chatbot/history/${threadId}`).pipe(
      tap(response => {
        const messages: Message[] = response.messages.map(msg => ({
          role: msg.role as 'user' | 'assistant',
          content: msg.content,
          timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
        }));
        
        this.conversations.update(all => {
          return all.map(conv => {
            if (conv.id === threadId) {
              return { ...conv, messages: messages };
            }
            return conv;
          });
        });
      })
    );
  }

  /**
   * Envía mensaje permitiendo forzar el 'domain' ('technical' | 'help')
   */
  sendMessage(message: string, resumeHitl: boolean = false, motorRef?: string, domain?: ThreadDomain): Observable<ChatResponse> {
    const threadId = this.currentConversationId();
    if (!threadId) {
      throw new Error('No active conversation');
    }

    const userId = this.authService.userSignal()?._id || 'unknown_user';

    const request: ChatRequest = {
      message,
      user_id: userId,
      thread_id: threadId,
      motor_ref: motorRef,
      resume_hitl: resumeHitl,
      domain: domain || undefined, // Si se omite, el backend ejecutará el clasificador
      company_id: this.authService.userSignal()?.companyId?._id || undefined
    };

    return this.http.post<ChatResponse>(`${environment.API_CHATBOT_URL}/api-chatbot/chat/invoke`, request).pipe(
      tap(response => {
        const assistantMessage: Message = {
          role: 'assistant',
          content: response.content,
          timestamp: new Date(),
          pending_interrupt: response.pending_interrupt || undefined
        };
        
        // Actualizar el dominio asignado en la conversación activa si el backend lo resolvió
        if (response.domain) {
          this.conversations.update(all => all.map(conv => 
            conv.id === threadId ? { ...conv, type: response.domain } : conv
          ));
        }

        this.addMessage(assistantMessage);
      })
    );
  }

  sendInitialMessage(
    message: string, 
    motorRef?: string, 
    details?: { brand: string, model: string, serialNumber: string, type: 'motor' | 'equipo' },
    domain?: ThreadDomain
  ) {
    this.pendingQuestion.set(message);
    this.pendingMotorRef.set(motorRef || null);
    this.pendingDomain.set(domain || null);
    this.pendingItemDetails.set(details || null);
  }

  createNewConversation(domain?: ThreadDomain) {
    const newConv: ChatConversation = {
      id: crypto.randomUUID(),
      title: 'Nueva conversación',
      type: domain || 'technical',
      messages: [{ role: 'assistant', content: '¡Hola! Soy tu asistente IA. ¿En qué puedo ayudarte hoy?', timestamp: new Date() }],
      lastUpdated: new Date()
    };
    this.conversations.update(prev => [newConv, ...prev]);
    this.currentConversationId.set(newConv.id);
  }

  selectConversation(id: string) {
    this.currentConversationId.set(id);
  }

  addMessage(message: Message) {
    const currentId = this.currentConversationId();
    if (!currentId) return;

    this.conversations.update(all => {
      return all.map(conv => {
        if (conv.id === currentId) {
          const updatedMessages = [...conv.messages, message];
          let title = conv.title;
          if (title === 'Nueva conversación' && message.role === 'user') {
            title = message.content.substring(0, 30) + (message.content.length > 30 ? '...' : '');
          }
          return { ...conv, messages: updatedMessages, title, lastUpdated: new Date() };
        }
        return conv;
      });
    });
  }

  getCurrentMessages(): Message[] {
    const conv = this.conversations().find(c => c.id === this.currentConversationId());
    return conv ? conv.messages : [];
  }
  
  deleteConversation(id: string): Observable<any> {
    const userId = this.authService.userSignal()?._id;
    if (!userId) {
      throw new Error('No user logged in');
    }

    return this.http.delete(`${environment.API_CHATBOT_URL}/api-chatbot/delete/${userId}/${id}`).pipe(
      tap(() => {
        this.conversations.update(prev => prev.filter(c => c.id !== id));
        if (this.currentConversationId() === id) {
          this.currentConversationId.set(null);
        }
      })
    );
  }
}